import {Component, ConfigOptions, jsx} from 'panel';
import isEqual from 'lodash/isEqual';
import isNumber from 'lodash/isNumber';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {FunnelQueryResponse} from 'common/report/funnels/models/api-types';
import {CountType, FunnelBookmark, FunnelSegment} from 'common/report/funnels/models/types';
import {FunnelsQuery} from 'common/report/funnels/models/funnels-query';
import {getMedianAccessor, getTTCShiftedEventIndex} from 'common/report/funnels/models/time-to-convert/utils';
import {processFunnelForChartWidget} from 'common/report/funnels/models/aggregates';
import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {SegmentFilterController, SegmentItem} from 'common/charts/contexts/segment-filter-controller';
import {ChartType, REPORT_LIMITS, ReportName, SavedLegend} from 'common/report/constants';
import {MultiMetricChartEl} from 'common/charts/visualization/multi-metric';
import {MetricFormatType, MetricSegment} from 'common/charts/visualization/multi-metric/types';
import {TwoStepDiagramEl} from 'common/charts/misc/two-step-diagram';
import {getFunnelStep} from 'common/report/funnels/models/bar-chart/utils';
import {OVERALL_PROP} from 'common/report/funnels/models/constants';
import {getBookmarkWithAppliedDefaults} from 'common/report/funnels/util/bookmark-overrides';
import {LiftType} from 'common/report/util/lift/types';
import {CurrentProject, CurrentProjectResource} from 'common/report/model-resources/projects';
import {PopoverCallback} from 'common/charts/types';
import {ContextMenuEl} from 'common/widgets/context-menu';
import {OTHER_SEGMENT_VALUE} from 'common/report/insights/util/constants';
import {getOffsetParent} from 'common/util/dom';
import {TooltipItemType} from 'common/components/chart-tooltip/types';
import {ChartTooltipAttrs} from 'common/components/chart-tooltip';
import {commaizeNumber} from 'common/util';
import {getPercentLabel} from 'common/charts/visualization/conversion-column-viz/util';

import {
  FunnelsStepProjectDetails,
  getDataTableInfo,
  getLegendItemsWithDataTableRows,
  OVERALL_CONTROL_GROUP,
} from '../funnels-step-chart/util';
import {getBaselineSegment} from '../funnels-step-chart/table-util';
import css from './index.inline.styl';

export interface FunnelsStepsMetricWidgetJsxProps {
  props: {
    bookmark: FunnelBookmark;
    funnelsResponse: FunnelQueryResponse;

    // project details like projectAsOfDate are typically attributes instead of an object prop, but projectAsOfDate supports null/empty as a valid value
    // which makes it impractical for a 'project-as-of-date' attribute to distinguish between "set to null/empty" and "not set, default to Store"
    projectDetails?: FunnelsStepProjectDetails | null;
    popoverCallback?: PopoverCallback;
  };
}
interface StepsMetricState {
  formattedData: FunnelSegment[];
  formattedCompareData: FunnelSegment[];
  formattedLiftData: FunnelSegment[];
  savedLegend: SavedLegend[];
  stepIndex: number;
  query: FunnelsQuery;
  dateRangeLabels: string[];
  legendSegmentItems: SegmentItem[];
  popoverStyles?: {
    visibility?: string;
    left?: string;
    top?: string;
  } | null;
  popoverSegment?: MetricSegment | null;
}

export class FunnelsStepsMetric extends Component<StepsMetricState, EmptyObj, any, any, ChartsContextRegistry> {
  private _funnelsResponse: FunnelQueryResponse;
  private _bookmark: FunnelBookmark;
  private _projectDetails: FunnelsStepProjectDetails | null = null;

  connectedCallback() {
    super.connectedCallback();
    this.processData();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  resourceCustomSubscriptions = new Map([[CurrentProjectResource, () => !this._projectDetails && this.processData()]]);

  private getCurrentProject(): CurrentProject {
    return this.getContext(`currentProject`).get().value;
  }

  private get segmentFilterController(): SegmentFilterController {
    return this.getContext(`segmentFilterController`);
  }

  public popoverCallback: PopoverCallback | null = null;

  get config(): ConfigOptions<StepsMetricState, any, ChartsContextRegistry> {
    return {
      useShadowDom: true,
      css,
      contexts: [`segmentFilterController` as const, `currentProject` as const],
      defaultContexts: {
        segmentFilterController: new SegmentFilterController(),
      },
      template: () => this.renderTemplate(),
    };
  }

  public set funnelsResponse(response: FunnelQueryResponse) {
    if (!isEqual(this._funnelsResponse, response)) {
      this._funnelsResponse = response;
      this.processData();
    }
  }

  public set bookmark(bookmark: FunnelBookmark) {
    const updatedBookmark = getBookmarkWithAppliedDefaults(bookmark);
    if (!isEqual(this._bookmark, updatedBookmark)) {
      this._bookmark = updatedBookmark;
      this.processData();
    }
  }

  public set projectDetails(value: FunnelsStepProjectDetails | null) {
    if (!isEqual(this._projectDetails, value)) {
      this._projectDetails = value;
      this.processData();
    }
  }

  private getProjectDetails(): null | FunnelsStepProjectDetails {
    if (this._projectDetails) {
      return this._projectDetails;
    }
    const currentProject = this.getCurrentProject();
    if (currentProject) {
      return {
        projectId: currentProject.id.toString(),
        projectAsOfDate: currentProject.projectAsOfDate,
        projectTimeZone: currentProject.timezone,
      };
    }
    return null;
  }

  private hasRequiredParams() {
    return this._funnelsResponse && this._bookmark && this.isConnected && this.getProjectDetails();
  }

  private processData() {
    if (!this.hasRequiredParams()) {
      return;
    }

    const stepIndex = getTTCShiftedEventIndex(this._bookmark.eventIndex);
    const projectDetails = this.getProjectDetails();
    const {legendSegmentItems, stateUpdates, savedLegend} = processFunnelForChartWidget(
      this._bookmark,
      this._funnelsResponse,
      projectDetails.projectId,
      projectDetails.projectAsOfDate,
      stepIndex,
      getMedianAccessor(stepIndex),
      projectDetails.projectTimeZone,
    );

    this.update({
      ...stateUpdates,
      legendSegmentItems,
      savedLegend,
    });

    this.updateLegend();
  }

  private updateLegend(): void {
    const {formattedData, formattedCompareData, formattedLiftData, savedLegend, legendSegmentItems} = this.state;
    if (formattedData) {
      const {rows, comparisonRows, columnGroups} = getDataTableInfo({
        bookmark: this._bookmark,
        projectDetails: this.getProjectDetails(),
        onStatSigClick: (_, row) => {
          if (row.props[0].prop === OVERALL_PROP) {
            this.dispatchEvent(new CustomEvent(`changeControlGroup`, {detail: {props: OVERALL_CONTROL_GROUP}}));
          } else {
            this.dispatchEvent(new CustomEvent(`changeControlGroup`, {detail: {props: row.props}}));
          }
        },
        formattedData,
        formattedCompareData,
        formattedLiftData,
        isDownloadMode: false,
        savedLegend,
        showStepNamesInGroupHeaders: true,
      });

      this.segmentFilterController.loadSections(
        [
          {
            items: getLegendItemsWithDataTableRows({
              rows,
              comparisonRows,
              legendSegmentItems,
            }),
            tableColumns: columnGroups,
          },
        ],
        {
          tableSortConfig: this._bookmark.sorting,
          defaultNumSelections: REPORT_LIMITS[ReportName.Funnels].segments[ChartType.FunnelStepsMetric].default,
          maxNumSelections: REPORT_LIMITS[ReportName.Funnels].segments[ChartType.FunnelStepsMetric].max,
        },
      );
      this.segmentFilterController.segmentationQueryLimit = REPORT_LIMITS[ReportName.Funnels].query;
      this.segmentFilterController.exportSegmentationQueryLimit = REPORT_LIMITS[ReportName.Funnels].csvExport;

      if (savedLegend) {
        this.segmentFilterController.loadFromSavedLegend(savedLegend);
      }
    }
  }

  private getMetricSegments(): MetricSegment[] {
    const {formattedData, formattedCompareData, formattedLiftData} = this.state;

    const stepIndex = getTTCShiftedEventIndex(this._bookmark.eventIndex);

    const segmentDataLookup = new Map(formattedData.map((data) => [data.prop, data]));
    const compareSegmentDataLookup = new Map(formattedCompareData?.map((data) => [data.prop, data]));
    const liftSegmentDataLookup = new Map(formattedLiftData?.map((data) => [data.prop, data]));

    const ratioAccessor = stepIndex === null ? `overall_conv_ratio` : `step_conv_ratio`;

    return this.segmentFilterController
      .getSelectedItems(0)
      .map(
        (item): MetricSegment => {
          const mainSegment = segmentDataLookup.get(item.id);
          const compareSegment = compareSegmentDataLookup.get(item.id);

          let comparisonValue = compareSegment ? getFunnelStep(compareSegment.steps, stepIndex)[ratioAccessor] : null;

          let liftComparisonValue;
          if (this._bookmark.liftComparison) {
            const liftComparisonSegment = liftSegmentDataLookup?.get(item.id);
            liftComparisonValue = getFunnelStep(liftComparisonSegment.steps, stepIndex)[ratioAccessor];

            let segmentLookupKey;

            switch (this._bookmark.liftComparison.type) {
              case LiftType.Time:
                segmentLookupKey = item.id;
                comparisonValue = getFunnelStep(compareSegment.steps, stepIndex)[ratioAccessor];
                break;
              case LiftType.Segment: {
                const segmentProps = mainSegment.props.map((prop) => prop.prop);
                const baselineSegment = getBaselineSegment({
                  funnelSegments: formattedData,
                  segmentProps,
                  groupValue: this._bookmark.liftComparison.groupValue,
                });
                segmentLookupKey = baselineSegment?.prop;
                comparisonValue = segmentLookupKey
                  ? getFunnelStep(segmentDataLookup.get(segmentLookupKey).steps, stepIndex)[ratioAccessor]
                  : 0;
                break;
              }
            }
          }

          return {
            value: getFunnelStep(mainSegment.steps, stepIndex)[ratioAccessor],
            stepIndex: stepIndex || mainSegment.steps.length - 1,
            comparisonValue,
            path: [mainSegment.prop],
            label: mainSegment.propName,
            color: this.segmentFilterController.getItemColor(0, mainSegment.prop),
            subLabel: `Completion Rate`,
            liftComparisonValue,
          };
        },
      )
      .filter(Boolean);
  }

  private renderTemplate() {
    if (!this.hasRequiredParams() || !this.state.formattedData) {
      return <div sel='.root' />;
    }

    const metrics = this.getMetricSegments();

    return (
      <div sel='.root'>
        <TwoStepDiagramEl attrs={{elref: `stepsDiagram`, 'step-names': this.getSelectedStepNames()}} />
        <MultiMetricChartEl
          attrs={{
            elref: `multiMetric`,
            metrics,
            'lift-type': this._bookmark?.liftComparison?.type,
            'hide-tooltip': !!this.state.popoverSegment,
            'selected-metric': this.state.popoverSegment,
          }}
          props={{
            metricFormatter: MetricFormatType.Percent,
            onMetricChartClick: this.popoverCallback && this.handleClick,
            tooltipDataCallback: (segment) => this.getTooltipData(segment),
          }}
        />
        {this.renderPopover()}
      </div>
    );
  }

  renderPopover() {
    const {popoverSegment, popoverStyles} = this.state;

    if (!popoverSegment || !this.popoverCallback) {
      return <></>;
    }

    const label = popoverSegment.label;
    return (
      <ContextMenuEl
        attrs={{
          'menu-items': this.popoverCallback(label),
          'relative-positioning': true,
          open: true,
          position: {left: 0, top: 0},
        }}
        style={popoverStyles}
        on={{
          action: (ev: CustomEvent) => this.handlePopoverAction(ev),
          close: () => this.handlePopoverClose(),
        }}
      />
    );
  }

  handleClick = (ev, metricSegment) => {
    ev.stopPropagation();

    const {pageX, pageY} = ev;
    const {label} = metricSegment;

    // don't support actions for "Other"
    if (label === OTHER_SEGMENT_VALUE || !this.popoverCallback) {
      return;
    }

    const parentRect = getOffsetParent(this).getBoundingClientRect();

    const gap = 12;
    const left = pageX - parentRect.x - window.scrollX + gap;
    const top = pageY - parentRect.y - window.scrollY + gap;

    const hasPopoverActions = this.popoverCallback?.call(null, label).length;
    if (hasPopoverActions) {
      this.update({
        popoverStyles: {left: `${left}px`, top: `${top}px`},
        popoverSegment: metricSegment,
      });
    }
  };

  handlePopoverAction(ev: CustomEvent) {
    const {stepIndex, color, path, comparisonValue} = this.state.popoverSegment;
    const {formattedCompareData, dateRangeLabels} = this.state;

    this.dispatchEvent(
      new CustomEvent(`popoverAction`, {
        detail: {
          action: ev.detail.action,
          color,
          stepIndex,
          segment: path[path.length - 1],
          isComparison: !!comparisonValue,
          comparisonDateRange: formattedCompareData ? dateRangeLabels[1] : null,
        },
      }),
    );
    this.handlePopoverClose();
  }

  handlePopoverClose() {
    this.update({
      popoverStyles: null,
      popoverSegment: null,
    });
  }

  private getTooltipData(segment: MetricSegment): ChartTooltipAttrs {
    const {color, stepIndex, path, label} = segment;
    const {formattedData, query} = this.state;
    const metricKey = path[path.length - 1];
    const metricData = formattedData.find((data) => data.prop === metricKey);
    const percent = metricData.steps[stepIndex][`overall_conv_ratio`];
    const count = metricData.steps[stepIndex].count;
    let countDescription;
    switch (query.countType) {
      case CountType.Unique:
        countDescription = `uniques`;
        break;
      case CountType.Session:
        countDescription = `sessions`;
        break;
      case CountType.General:
      default:
        countDescription = `total times`;
        break;
    }

    const dataRows = [
      {
        items: [
          {text: getPercentLabel(percent), type: TooltipItemType.Data},
          {text: `converted`, type: TooltipItemType.Text},
        ],
      },
      {
        items: [
          {text: commaizeNumber(count), type: TooltipItemType.Data},
          {text: countDescription, type: TooltipItemType.Text},
        ],
      },
    ];

    return {
      sections: [
        {
          rows: [{items: [{text: label, type: TooltipItemType.Strong}]}],
        },
        {
          rows: dataRows,
        },
      ],
      'title-color': color,
    };
  }

  private getSelectedStepNames(): [string, string] {
    if (!this.state.query) {
      return [``, ``];
    }

    const {eventIndex} = this._bookmark;
    const stepLabels = this.state.query.getStepLabels();

    if (isNumber(eventIndex)) {
      const [step1, step2] = stepLabels.slice(eventIndex, eventIndex + 2);
      return [`${eventIndex + 1}. ${step1}`, `${eventIndex + 2}. ${step2}`];
    }

    return [`1. ${stepLabels[0]}`, `${stepLabels.length}. ${stepLabels[stepLabels.length - 1]}`];
  }
}

export const FunnelsStepsMetricWidgetEl = defineAndMakeJsxFactory<FunnelsStepsMetricWidgetJsxProps>(
  `mp-funnels-steps-metric-widget`,
  FunnelsStepsMetric,
);
export const FunnelsStepsMetricWidgetReact = panel2react<FunnelsStepsMetricWidgetJsxProps>(
  `mp-funnels-steps-metric-widget`,
  FunnelsStepsMetric,
);
